import * as THREE from 'three';
import { Euler, Quaternion, Vector3 } from 'three';

import { CameraTransformation } from '../../../../typings/api/skymap/rest/v0/.common';
import { ElementObj } from '../elements/element-obj';
import { KeyInputData } from '../utilities/input-event/input-event-data-extractor';
import { InputHandlerInterface } from '../utilities/input-event/input-handler-interface';

export enum CameraControllerStateType {
  Idle,
  Panning,
  Tweening,
  Rotating,
}

export interface CameraControllerInterface extends InputHandlerInterface {
  focusOnBox(bb: THREE.Box3): void;
  focusOnAsset(asset: ElementObj | undefined): void;
  getFocusedAsset(): THREE.Object3D | undefined;

  focusOnScene(): void;

  isTransitioning(): boolean;

  /** A key went down that the active tool didn't use. */
  onKeyDown?(input: KeyInputData): void;
  onKeyUp?(input: KeyInputData): void;
  /**
   * Moves the camera between input events, on each frame, e.g. while a key is held. Returns true
   * if the camera moved.
   */
  update?(): boolean;
  /** Releases what the controller holds, once another controller replaces it. */
  dispose?(): void;
}

export enum CameraType {
  FPSCamera,
  PerspectiveCamera,
  OrthographicCamera,
}

export type PerspectiveCameraParameters = {
  /**
   * Focus point position. This is what the camera is looking at
   * and acts as the pivot point for rotation.
   */
  focusPointPosition: Vector3;
  focusPointRotation: Euler;
  /**
   * The distance from the camera to the focus point.
   */
  cameraDistanceToFocusPoint: number;
  aspect: number;
};

export type FpsCameraParameters = {
  aspect: number;
};

export type OrthographicCameraParameters = {
  position: Vector3;
  height: number;
};

export interface SkyviewCamera {
  uuid: string;
  cameraType: CameraType;
  move(vector: THREE.Vector3): void;
  rotate(phiDelta: number, thetaDelta: number, target?: THREE.Vector3): void;

  timeSinceTransformed(): number;

  zooming(deltaZoom: number): void;
  setZoom(zoom: number): void;

  updateAspect(width: number, height: number): void;
  setParameters(
    cameraParameter:
      | PerspectiveCameraParameters
      | OrthographicCameraParameters
      | FpsCameraParameters,
  ): void;

  getDistanceToPoint(point: THREE.Vector3): number;
  getDistanceToBoundingBox(box: THREE.Box3): number;

  focusOnBox(box: THREE.Box3, offset: number): void;

  getScreenPosition(point: THREE.Vector3): THREE.Vector2;
  getLookAtPosition(): THREE.Vector3;

  getFrustum(): THREE.Frustum;
  getRay(x: number, y: number): THREE.Raycaster;

  setPosition(offset: THREE.Vector2): void;

  copyViewFromCamera(camera: SkyviewCamera, skipOrigoTransformation?: boolean): void;

  addToScene(scene: THREE.Scene): void;
  removeFromScene(): void;

  transformTo(position?: Vector3, rotation?: Quaternion, duration?: number): void;

  getTHREECamera(): THREE.PerspectiveCamera | THREE.OrthographicCamera;

  extractTransformation(): CameraTransformation;
  applyTransformation(transformation: CameraTransformation): void;
}
